import {
    Box,
    Chip,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { GridRenderCellParams } from "@mui/x-data-grid-pro";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import AcxButton from "components/UI/AcxButton";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import { useStore } from "utils/useStore";
import AgentSkillsStore from "components/AgentSkills/Stores/AgentSkillsStore";
import AgentSkillLibrarySyncDialog from "components/AgentSkills/Views/AgentSkillLibrarySyncDialog";
import AgentSkillHistoryDialog from "components/AgentSkills/Views/AgentSkillHistoryDialog";
import {
    AgentSkillLibraryItem,
    AgentSkillLibraryStatus,
    AgentSkillListItem,
    AgentSkillScope,
} from "components/AgentSkills/Models/AgentSkill";

const formatDateTime = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : "-";

const scopeLabels: Record<AgentSkillScope, string> = {
    KboChat: "KBo Chat",
    KboFlow: "KBo Flow",
};

const libraryStatusLabels: Record<AgentSkillLibraryStatus, string> = {
    Missing: "Not installed",
    CurrentLibraryVersion: "Up to date",
    DifferentVersion: "Update available",
    TenantOwned: "Tenant owned",
    NameConflict: "Name conflict",
    DuplicateActiveVersions: "Duplicate active versions",
    HistoricalIdConflict: "Historical ID conflict",
    Legacy: "Resave to update",
};

const libraryStatusDescriptions: Record<AgentSkillLibraryStatus, string> = {
    Missing: "No tenant skill is installed.",
    CurrentLibraryVersion: "This tenant already has the current library version.",
    DifferentVersion: "A newer library version is available.",
    TenantOwned: "Tenant-owned skills cannot be overwritten from the library.",
    NameConflict: "Another active tenant skill already has this name.",
    DuplicateActiveVersions: "Resolve the tenant's duplicate active versions first.",
    HistoricalIdConflict:
        "An inactive historical tenant record already has this library version's ID. To preserve version history, resave the library skill to create a new version, then sync it.",
    Legacy: "Resave this library skill to create a version group.",
};

const yourSkillsLibraryStatusLabels: Record<AgentSkillLibraryStatus, string> = {
    ...libraryStatusLabels,
    DifferentVersion: "Updated",
};

const yourSkillsLibraryStatusDescriptions: Record<
    AgentSkillLibraryStatus,
    string
> = {
    ...libraryStatusDescriptions,
    DifferentVersion:
        "This tenant skill differs from the current library version.",
};

const libraryStatusColor = (status: AgentSkillLibraryStatus) => {
    switch (status) {
        case "DifferentVersion":
            return "warning" as const;
        case "CurrentLibraryVersion":
            return "success" as const;
        case "TenantOwned":
        case "NameConflict":
        case "DuplicateActiveVersions":
        case "HistoricalIdConflict":
        case "Legacy":
            return "error" as const;
        default:
            return "default" as const;
    }
};

const getLibraryStatusLabel = (librarySkill: AgentSkillLibraryItem) =>
    librarySkill.restoresLibraryVersionFromHistory
        ? "Restore available"
        : librarySkill.status
            ? libraryStatusLabels[librarySkill.status]
            : "Status unavailable";

const getLibraryStatusDescription = (librarySkill: AgentSkillLibraryItem) =>
    librarySkill.restoresLibraryVersionFromHistory
        ? "This tenant has a newer version. Restoring will reactivate the previous library version from history and retire the tenant version."
        : librarySkill.status
            ? libraryStatusDescriptions[librarySkill.status]
            : "Status unavailable";

const getYourSkillsLibraryStatusLabel = (librarySkill: AgentSkillLibraryItem) =>
    librarySkill.restoresLibraryVersionFromHistory
        ? "Tenant newer"
        : librarySkill.status
            ? yourSkillsLibraryStatusLabels[librarySkill.status]
            : "";

const getYourSkillsLibraryStatusDescription = (
    librarySkill: AgentSkillLibraryItem,
) =>
    librarySkill.restoresLibraryVersionFromHistory
        ? "This tenant version is newer than the library version. Restoring will reactivate the previous library version from history."
        : librarySkill.status
            ? yourSkillsLibraryStatusDescriptions[librarySkill.status]
            : "";

const renderScopes = (scopes: AgentSkillScope[]) =>
    scopes.length > 0 ? (
        <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
        >
            {scopes.map((scope) => (
                <Chip
                    key={scope}
                    size="small"
                    label={scopeLabels[scope]}
                    variant="outlined"
                />
            ))}
        </Stack>
    ) : (
        <Chip size="small" label="No scopes" color="warning" variant="outlined" />
    );

const createDataGridStore = (gridId: string, rowTerm: string) => {
    const dataGridStore = new AcxDataGridStore(gridId, "Administrative");
    dataGridStore.checkboxSelection = false;
    dataGridStore.disableRowSelectionOnClick = true;
    dataGridStore.pagination = true;
    dataGridStore.rowTerm = rowTerm;
    dataGridStore.onPaginationModelChange = (model) => {
        dataGridStore.paginationModel = model;
    };
    dataGridStore.heightOverride = "clamp(280px, 40vh, 480px)";
    return dataGridStore;
};

const AgentSkillsPage: React.FC = observer(() => {
    const store = useStore(AgentSkillsStore);
    const navigate = useNavigate();
    const [deleteTarget, setDeleteTarget] = useState<AgentSkillListItem | null>(
        null,
    );
    const [isLibrarySyncOpen, setIsLibrarySyncOpen] = useState(false);
    const [historyTarget, setHistoryTarget] = useState<AgentSkillListItem | null>(
        null,
    );
    const yourSkillsDataGridStore = useMemo(
        () => createDataGridStore("AgentSkillsYourSkills", "skills"),
        [],
    );
    const libraryDataGridStore = useMemo(
        () => createDataGridStore("AgentSkillsLibrarySkills", "library skills"),
        [],
    );

    const listLoading = store.getTaskLoading(AgentSkillsStore.Tasks.Load);
    const libraryLoading = store.getTaskLoading(
        AgentSkillsStore.Tasks.LoadLibrary,
    );
    const addToLibraryLoading = store.getTaskLoading(
        AgentSkillsStore.Tasks.AddToLibrary,
    );
    const removeFromLibraryLoading = store.getTaskLoading(
        AgentSkillsStore.Tasks.RemoveFromLibrary,
    );
    const canPublishToLibrary =
        store.isUltraUser && store.isCurrentOrganizationAuthcx;
    const librarySkillByTenantSkillId = useMemo(
        () =>
            new Map(
                store.librarySkills
                    .filter((librarySkill) => librarySkill.tenantSkillId)
                    .map((librarySkill) => [
                        librarySkill.tenantSkillId!,
                        librarySkill,
                    ]),
            ),
        [store.librarySkills],
    );

    const reloadAll = useCallback(() => {
        store.loadSkills();
        store.loadLibrary();
    }, [store]);

    // Reload when the active organization changes.
    useEffect(() => {
        reloadAll();
    }, [reloadAll, store.currentOrganizationId]);

    const openNew = () => {
        navigate("new");
    };

    const openEdit = useCallback((id: string) => {
        navigate(id);
    }, [navigate]);

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        await store.deleteSkill(deleteTarget.id);
        setDeleteTarget(null);
        reloadAll();
    };

    const renderLibraryAction = useCallback(
        (librarySkill: AgentSkillLibraryItem) => {
            if (store.isCurrentOrganizationAuthcx) {
                return (
                    <Chip
                        size="small"
                        label="Source library"
                        variant="outlined"
                    />
                );
            }

            if (librarySkill.status === "Missing") {
                return (
                    <AcxButton
                        acxTheme="secondary"
                        loading={store.getTaskLoading(
                            AgentSkillsStore.Tasks.AddFromLibrary(
                                librarySkill.id,
                            ),
                        )}
                        onClick={() => store.addFromLibrary(librarySkill)}
                    >
                        Add Skill
                    </AcxButton>
                );
            }

            const updateAvailable = librarySkill.status === "DifferentVersion";
            const actionLabel = librarySkill.restoresLibraryVersionFromHistory
                ? "Restore Skill"
                : "Update Skill";
            return (
                <Tooltip
                    title={getLibraryStatusDescription(librarySkill)}
                >
                    <span>
                        <AcxButton
                            acxTheme="secondary"
                            loading={store.getTaskLoading(
                                AgentSkillsStore.Tasks.UpdateFromLibrary(
                                    librarySkill.id,
                                ),
                            )}
                            disabled={!updateAvailable}
                            onClick={() => store.updateFromLibrary(librarySkill)}
                        >
                            {actionLabel}
                        </AcxButton>
                    </span>
                </Tooltip>
            );
        },
        [store],
    );

    const yourSkillsColumns = useMemo<IColDef[]>(
        () => [
            {
                field: "name",
                headerName: "Name",
                flex: 1,
                minWidth: 180,
                display: "flex",
            },
            {
                field: "description",
                headerName: "Description",
                flex: 2,
                minWidth: 240,
                display: "flex",
                renderCell: (
                    params: GridRenderCellParams<AgentSkillListItem>,
                ) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                    >
                        {params.row.description}
                    </Typography>
                ),
            },
            {
                field: "librarySkillName",
                headerName: "Library Skill",
                flex: 1,
                minWidth: 180,
                display: "flex",
                valueGetter: (_, row: AgentSkillListItem) =>
                    librarySkillByTenantSkillId.get(row.id)?.name ?? "",
                renderCell: (
                    params: GridRenderCellParams<AgentSkillListItem>,
                ) => {
                    const librarySkill = librarySkillByTenantSkillId.get(
                        params.row.id,
                    );
                    return (
                        <Typography variant="body2" noWrap>
                            {librarySkill?.name ?? "—"}
                        </Typography>
                    );
                },
            },
            {
                field: "libraryStatus",
                headerName: "Library Status",
                width: 170,
                display: "flex",
                valueGetter: (_, row: AgentSkillListItem) => {
                    const librarySkill = librarySkillByTenantSkillId.get(row.id);
                    return librarySkill
                        ? getYourSkillsLibraryStatusLabel(librarySkill)
                        : "";
                },
                renderCell: (
                    params: GridRenderCellParams<AgentSkillListItem>,
                ) => {
                    const librarySkill = librarySkillByTenantSkillId.get(
                        params.row.id,
                    );
                    const status = librarySkill?.status;
                    return librarySkill && status ? (
                        <Tooltip title={getYourSkillsLibraryStatusDescription(librarySkill)}>
                            <Chip
                                size="small"
                                label={getYourSkillsLibraryStatusLabel(librarySkill)}
                                color={libraryStatusColor(status)}
                                variant="outlined"
                            />
                        </Tooltip>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    );
                },
            },
            {
                field: "fileCount",
                headerName: "Files",
                type: "number",
                width: 80,
                display: "flex",
            },
            {
                field: "isPublished",
                headerName: "Publish Status",
                width: 130,
                display: "flex",
                valueGetter: (_, row: AgentSkillListItem) =>
                    row.isPublished ? "Published" : "Draft",
                renderCell: (
                    params: GridRenderCellParams<AgentSkillListItem>,
                ) =>
                    params.row.isPublished ? (
                        <Chip
                            size="small"
                            label="Published"
                            color="success"
                            variant="outlined"
                        />
                    ) : (
                        <Chip size="small" label="Draft" variant="outlined" />
                    ),
            },
            {
                field: "scopes",
                headerName: "Scopes",
                flex: 1,
                minWidth: 180,
                display: "flex",
                valueGetter: (_, row: AgentSkillListItem) =>
                    row.scopes.map((scope) => scopeLabels[scope]).join(", "),
                renderCell: (
                    params: GridRenderCellParams<AgentSkillListItem>,
                ) => renderScopes(params.row.scopes),
            },
            {
                field: "modifiedOn",
                headerName: "Modified",
                width: 180,
                display: "flex",
                valueGetter: (_, row: AgentSkillListItem) =>
                    row.modifiedOn ?? row.createdOn ?? "",
                renderCell: (
                    params: GridRenderCellParams<AgentSkillListItem>,
                ) => formatDateTime(params.row.modifiedOn ?? params.row.createdOn),
            },
            {
                field: "actions",
                headerName: "Actions",
                width: canPublishToLibrary ? 190 : 135,
                display: "flex",
                sortable: false,
                filterable: false,
                align: "right",
                headerAlign: "right",
                renderCell: (
                    params: GridRenderCellParams<AgentSkillListItem>,
                ) => {
                    const skill = params.row;
                    return (
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            sx={{ width: "100%" }}
                        >
                            <Tooltip title="Edit skill">
                                <IconButton
                                    aria-label="Edit skill"
                                    color="primary"
                                    size="small"
                                    onClick={() => openEdit(skill.id)}
                                >
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="View version history">
                                <IconButton
                                    aria-label="View version history"
                                    color="primary"
                                    size="small"
                                    onClick={() => setHistoryTarget(skill)}
                                >
                                    <HistoryOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            {canPublishToLibrary && (
                                <Tooltip
                                    title={
                                        skill.isInLibrary
                                            ? "Remove from library"
                                            : "Add to library"
                                    }
                                >
                                    <span>
                                        <IconButton
                                            aria-label={
                                                skill.isInLibrary
                                                    ? "Remove from library"
                                                    : "Add to library"
                                            }
                                            color="primary"
                                            size="small"
                                            disabled={
                                                skill.isInLibrary
                                                    ? addToLibraryLoading
                                                    : removeFromLibraryLoading
                                            }
                                            onClick={() =>
                                                skill.isInLibrary
                                                    ? store.removeFromLibrary(skill)
                                                    : store.addToLibrary(skill)
                                            }
                                        >
                                            {skill.isInLibrary ? (
                                                <RemoveCircleOutlineIcon fontSize="small" />
                                            ) : (
                                                <AddCircleOutlineIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            <Tooltip title="Delete skill">
                                <IconButton
                                    aria-label="Delete skill"
                                    color="error"
                                    size="small"
                                    onClick={() => setDeleteTarget(skill)}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    );
                },
            },
        ],
        [
            addToLibraryLoading,
            canPublishToLibrary,
            librarySkillByTenantSkillId,
            openEdit,
            removeFromLibraryLoading,
            store,
        ],
    );

    const libraryColumns = useMemo<IColDef[]>(
        () => [
            {
                field: "name",
                headerName: "Name",
                flex: 1,
                minWidth: 180,
                display: "flex",
            },
            {
                field: "description",
                headerName: "Description",
                flex: 2,
                minWidth: 240,
                display: "flex",
                renderCell: (
                    params: GridRenderCellParams<AgentSkillLibraryItem>,
                ) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                    >
                        {params.row.description}
                    </Typography>
                ),
            },
            {
                field: "fileCount",
                headerName: "Files",
                type: "number",
                width: 80,
                display: "flex",
            },
            {
                field: "status",
                headerName: "Status",
                width: 180,
                display: "flex",
                valueGetter: (_, row: AgentSkillLibraryItem) =>
                    store.isCurrentOrganizationAuthcx
                        ? "Source library"
                        : getLibraryStatusLabel(row),
                renderCell: (
                    params: GridRenderCellParams<AgentSkillLibraryItem>,
                ) =>
                    store.isCurrentOrganizationAuthcx ? (
                        <Tooltip title="Published from Authcx.">
                            <Chip
                                size="small"
                                label="Source library"
                                variant="outlined"
                            />
                        </Tooltip>
                    ) : params.row.status ? (
                        <Tooltip title={getLibraryStatusDescription(params.row)}>
                            <Chip
                                size="small"
                                label={getLibraryStatusLabel(params.row)}
                                color={libraryStatusColor(params.row.status)}
                                variant="outlined"
                            />
                        </Tooltip>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Status unavailable
                        </Typography>
                    ),
            },
            {
                field: "scopes",
                headerName: "Scopes",
                flex: 1,
                minWidth: 180,
                display: "flex",
                valueGetter: (_, row: AgentSkillLibraryItem) =>
                    row.scopes.map((scope) => scopeLabels[scope]).join(", "),
                renderCell: (
                    params: GridRenderCellParams<AgentSkillLibraryItem>,
                ) => renderScopes(params.row.scopes),
            },
            {
                field: "modifiedOn",
                headerName: "Modified",
                width: 180,
                display: "flex",
                valueGetter: (_, row: AgentSkillLibraryItem) =>
                    row.modifiedOn ?? "",
                renderCell: (
                    params: GridRenderCellParams<AgentSkillLibraryItem>,
                ) => formatDateTime(params.row.modifiedOn),
            },
            {
                field: "actions",
                headerName: "Actions",
                width: 150,
                display: "flex",
                sortable: false,
                filterable: false,
                align: "right",
                headerAlign: "right",
                renderCell: (
                    params: GridRenderCellParams<AgentSkillLibraryItem>,
                ) => (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        {renderLibraryAction(params.row)}
                    </Box>
                ),
            },
        ],
        [renderLibraryAction, store.isCurrentOrganizationAuthcx],
    );

    useEffect(() => {
        void yourSkillsDataGridStore.setColumns(yourSkillsColumns);
    }, [yourSkillsColumns, yourSkillsDataGridStore]);

    useEffect(() => {
        void libraryDataGridStore.setColumns(libraryColumns);
    }, [libraryColumns, libraryDataGridStore]);

    useEffect(() => {
        yourSkillsDataGridStore.setRows(store.skills);
        yourSkillsDataGridStore.setLoading(listLoading);
    }, [listLoading, store.skills, yourSkillsDataGridStore]);

    useEffect(() => {
        libraryDataGridStore.setRows(store.librarySkills);
        libraryDataGridStore.setLoading(libraryLoading);
    }, [libraryDataGridStore, libraryLoading, store.librarySkills]);

    return (
        <Box
            sx={{
                p: 3,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2, width: "100%" }}
            >
                <Box>
                    <Typography variant="h5">Agent Skills</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Reusable instructions KBo can load for a task.
                    </Typography>
                </Box>
                <AcxButton
                    color="primary"
                    onClick={openNew}
                    startIcon={<NoteAddIcon fontSize="small" />}
                >
                    New Skill
                </AcxButton>
            </Stack>

            <Typography variant="h6" sx={{ mb: 1 }}>
                Your Skills
            </Typography>
            <Box sx={{ mb: 4, width: "100%" }}>
                <AcxDataGrid dataGridStore={yourSkillsDataGridStore} />
            </Box>

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
            >
                <Box>
                    <Typography variant="h6">Skills Library</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Published Authcx skills that can be copied into customer
                        tenants.
                    </Typography>
                </Box>
                {canPublishToLibrary && (
                    <AcxButton
                        acxTheme="secondary"
                        onClick={() => setIsLibrarySyncOpen(true)}
                    >
                        Sync Skills to Tenants
                    </AcxButton>
                )}
            </Stack>
            <Box sx={{ mb: 4, width: "100%" }}>
                <AcxDataGrid dataGridStore={libraryDataGridStore} />
            </Box>

            <AcxConfirmationDialog
                isOpen={!!deleteTarget}
                title="Delete Skill"
                subTitle={deleteTarget?.name}
                content="This skill will be removed. This cannot be undone."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                onClose={() => setDeleteTarget(null)}
                onConfirm={confirmDelete}
            />
            {isLibrarySyncOpen && (
                <AgentSkillLibrarySyncDialog
                    isOpen
                    onClose={() => setIsLibrarySyncOpen(false)}
                />
            )}
            <AgentSkillHistoryDialog
                skill={historyTarget}
                onClose={() => setHistoryTarget(null)}
            />
        </Box>
    );
});

export default AgentSkillsPage;
